import axios from 'axios';

import { KanzhongBackend } from 'constant/KanzhongBackend';
import { ServerError, createApiError } from 'stores/ApiClientError';

// --- 型定義 ---

export type PasteColumnResult = {
  columnKey: string;
  displayName: string;
};

export type PasteDefinitionResult = {
  pasteDefinitionId: string;
  name: string;
  columns: PasteColumnResult[];
  createdAt: string;
  createdBy: string;
};

export type GetPasteDefinitionResponse = {
  results: PasteDefinitionResult[];
};

export type CreatePasteDefinitionParams = {
  name: string;
  columns: string[];
};

export type UpdatePasteDefinitionParams = {
  pasteDefinitionId: string;
  name: string;
  columns: string[];
};

export type DeletePasteDefinitionParams = {
  pasteDefinitionId: string;
};

// --- API関数 ---

/**
 * ペースト定義一覧取得API
 * リクエストボディなし。全件取得（name昇順）。
 */
export const getPasteDefinitions = async (): Promise<GetPasteDefinitionResponse> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(
      KanzhongBackend.GET_CONTRACT_PASTE_DEFINITION,
      {},
      {
        headers: {
          'X-Session-Id': sessionId,
        },
      },
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * ペースト定義登録API
 * @param params.name - ペースト名（必須, 最大200文字, 重複不可）
 * @param params.columns - カラムkeyの配列（必須, 1件以上, 配列の順序がsort_order）
 */
export const createPasteDefinition = async (
  params: CreatePasteDefinitionParams,
): Promise<string> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.CREATE_CONTRACT_PASTE_DEFINITION, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.pasteDefinitionId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * ペースト定義編集API
 * @param params.pasteDefinitionId - ペースト定義ID（必須, 最大64文字）
 * @param params.name - ペースト名（必須, 最大200文字, 重複不可（自分自身を除く））
 * @param params.columns - カラムkeyの配列（必須, 1件以上, 配列の順序がsort_order）
 */
export const updatePasteDefinition = async (params: UpdatePasteDefinitionParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.UPDATE_CONTRACT_PASTE_DEFINITION, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * ペースト定義削除API
 * @param params.pasteDefinitionId - ペースト定義ID（必須, 最大64文字）
 */
export const deletePasteDefinition = async (params: DeletePasteDefinitionParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.DELETE_CONTRACT_PASTE_DEFINITION, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};
